import React, { useState } from 'react';
import { X, Link2, Search, Calendar, User, MapPin } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const WorkPermitLinking = ({ onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPermits, setSelectedPermits] = useState([]);

  const availablePermits = [
    {
      id: 'PTW-2024-156',
      type: 'Hot Work',
      location: 'Module A - Compressor Area',
      description: 'Welding repair on flange connection',
      status: 'Active',
      issueDate: '2024-12-04',
      validUntil: '2024-12-05',
      issuer: 'John Smith',
      riskLevel: 'high',
    },
    {
      id: 'PTW-2024-178',
      type: 'Confined Space',
      location: 'Tank Farm - TK-201',
      description: 'Internal tank inspection and cleaning',
      status: 'Active',
      issueDate: '2024-12-04',
      validUntil: '2024-12-06',
      issuer: 'Sarah Johnson',
      riskLevel: 'critical',
    },
    {
      id: 'PTW-2024-189',
      type: 'Electrical Work',
      location: 'Substation B',
      description: 'Breaker replacement and testing',
      status: 'Pending Approval',
      issueDate: '2024-12-04',
      validUntil: '2024-12-05',
      issuer: 'Mike Chen',
      riskLevel: 'high',
    },
    {
      id: 'PTW-2024-201',
      type: 'Cold Work',
      location: 'Module C - Heat Exchanger',
      description: 'Gasket replacement on heat exchanger',
      status: 'Active',
      issueDate: '2024-12-03',
      validUntil: '2024-12-04',
      issuer: 'David Lee',
      riskLevel: 'medium',
    },
  ];

  const filteredPermits = availablePermits?.filter(permit =>
    searchQuery === '' ||
    permit?.id?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    permit?.type?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    permit?.location?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    permit?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const togglePermit = (permitId) => {
    setSelectedPermits(prev =>
      prev?.includes(permitId)
        ? prev?.filter(id => id !== permitId)
        : [...prev, permitId]
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      Active: 'bg-green-100 text-green-800 border-green-300',
      'Pending Approval': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      Expired: 'bg-red-100 text-red-800 border-red-300',
      Closed: 'bg-gray-100 text-gray-800 border-gray-300',
    };
    return colors?.[status] || colors?.Active;
  };

  const getRiskBadgeColor = (level) => {
    const colors = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800',
    };
    return colors?.[level] || colors?.medium;
  };

  const getPermitTypeIcon = (type) => {
    // Return appropriate color based on permit type
    const colors = {
      'Hot Work': 'text-red-600',
      'Confined Space': 'text-purple-600',
      'Electrical Work': 'text-yellow-600',
      'Cold Work': 'text-blue-600',
    };
    return colors?.[type] || 'text-gray-600';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link2 className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Link Work Permits</h2>
              <p className="text-sm text-gray-600 mt-1">Associate hazards with work permits</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-200">
          <Input
            type="text"
            placeholder="Search permits by ID, type, location, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            icon={<Search className="w-5 h-5 text-gray-400" />}
          />
        </div>

        {/* Selected Permits Summary */}
        {selectedPermits?.length > 0 && (
          <div className="px-6 py-3 bg-blue-50 border-b border-blue-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">
                {selectedPermits?.length} permit(s) selected
              </span>
              <button
                onClick={() => setSelectedPermits([])}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        {/* Permits List */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {filteredPermits?.map((permit) => {
              const isSelected = selectedPermits?.includes(permit?.id);
              
              return (
                <div
                  key={permit?.id}
                  onClick={() => togglePermit(permit?.id)}
                  className={`
                    p-5 rounded-lg border-2 cursor-pointer transition-all
                    ${isSelected
                      ? 'border-blue-500 bg-blue-50' :'border-gray-200 bg-white hover:border-gray-300'
                    }
                  `}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-3">
                      {/* Permit Header */}
                      <div className="flex items-center gap-3">
                        <Link2 className={`w-5 h-5 ${isSelected ? 'text-blue-600' : getPermitTypeIcon(permit?.type)}`} />
                        <span className="font-bold text-gray-900">{permit?.id}</span>
                        <span className={`px-2.5 py-1 rounded text-xs font-medium border ${getStatusColor(permit?.status)}`}>
                          {permit?.status}
                        </span>
                        <span className={`px-2.5 py-1 rounded text-xs font-medium ${getRiskBadgeColor(permit?.riskLevel)}`}>
                          {permit?.riskLevel?.toUpperCase()}
                        </span>
                      </div>

                      {/* Permit Type */}
                      <div className="text-sm font-semibold text-gray-700">{permit?.type}</div>

                      {/* Description */}
                      <p className="text-sm text-gray-600">{permit?.description}</p>

                      {/* Permit Details */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          {permit?.location}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <User className="w-4 h-4 text-gray-400" />
                          Issuer: {permit?.issuer}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          Valid: {permit?.issueDate} - {permit?.validUntil}
                        </div>
                      </div>
                    </div>

                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => togglePermit(permit?.id)}
                      className="w-5 h-5 text-blue-600 rounded ml-4"
                      onClick={(e) => e?.stopPropagation()}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">
            Select permits to link with identified hazards
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={selectedPermits?.length === 0}
            >
              Link Permits ({selectedPermits?.length})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkPermitLinking;